import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { InputField } from "@financial-edge/shared";
import api from "../api/api";
import AuthAlert from "../components/auth/AuthAlert";
import AuthContextPanel from "../components/auth/AuthContextPanel";
import AuthShell from "../components/auth/AuthShell";
import WebsiteApplicationIntro from "../components/auth/WebsiteApplicationIntro";
import PrimaryButton from "../components/layout/PrimaryButton";
import { getAuthErrorCode, getAuthRequestId, getSignInErrorMessage } from "../utils/auth/authDiagnostics";
import { logAuthFlowWarning } from "../utils/auth/authFlowLogger";
import { buildBlockedSignInStatusState, isBlockedSignInInterception } from "../utils/auth/interceptedAccess";
import {
    buildLaunchSearch,
    getIntentDetails,
    getEffectiveLaunchContext,
    isXPortalLayerLaunch,
    isEmailFirstIntent,
    persistLaunchContext,
    persistRememberDevicePreference,
    readRememberedSignInEmail,
    readRememberDevicePreference,
    resolveXPortalLayerIntentFromAuthResponse,
    resolveXPortalLayerReturnTo,
} from "../utils/auth/intent";
import { formatDisplayNumber, isValidAuMobile, isValidEmail, normalizePhone, parsePhoneInput, toUserId } from "../utils/auth/authUtils";
import { getOtpLengthForChallenge } from "../utils/auth/otpLength";
import { persistPendingVerificationContext } from "../utils/auth/pendingVerification";
import { useAuthPageMeta } from "../utils/ui/useAuthPageMeta";

const ARTICLELESS_PORTAL_NAMES = new Set(["iPortal", "xPortal", "cPortal", "sPortal"]);
const ARTICLE_PORTAL_NAMES = new Set(["Broker Portal"]);
const XPORTAL_LAYER_AUTH_CODE_LABELS = Object.freeze({
    BROKER_SESSION_REQUIRED: "XPORTAL_WORKSPACE_SESSION_REQUIRED",
    LENDER_SESSION_REQUIRED: "XPORTAL_WORKSPACE_SESSION_REQUIRED",
    BROKER_SESSION_REFRESH_FAILED: "XPORTAL_WORKSPACE_SESSION_REFRESH_FAILED",
    LENDER_SESSION_REFRESH_FAILED: "XPORTAL_WORKSPACE_SESSION_REFRESH_FAILED",
    BROKER_SESSION_VALIDATION_FAILED: "XPORTAL_WORKSPACE_SESSION_VALIDATION_FAILED",
    LENDER_SESSION_VALIDATION_FAILED: "XPORTAL_WORKSPACE_SESSION_VALIDATION_FAILED",
    BROKER_SESSION_INVALID: "XPORTAL_WORKSPACE_SESSION_INVALID",
    LENDER_SESSION_INVALID: "XPORTAL_WORKSPACE_SESSION_INVALID",
    BROKER_SESSION_EXPIRED: "XPORTAL_WORKSPACE_SESSION_EXPIRED",
    LENDER_SESSION_EXPIRED: "XPORTAL_WORKSPACE_SESSION_EXPIRED",
    BROKER_SESSION_REVOKED: "XPORTAL_WORKSPACE_SESSION_REVOKED",
    LENDER_SESSION_REVOKED: "XPORTAL_WORKSPACE_SESSION_REVOKED",
    BROKER_ACCESS_CHANGED: "XPORTAL_WORKSPACE_ACCESS_CHANGED",
    LENDER_ACCESS_CHANGED: "XPORTAL_WORKSPACE_ACCESS_CHANGED",
});
const WEBSITE_APPLICATION_INTRO_ACK_PARAM = "applicationIntro";
const WEBSITE_APPLICATION_INTRO_ACK_VALUE = "done";
const AUTH_ENVIRONMENT_HOSTS = Object.freeze({
    production: "financialedge-login.vercel.app",
    staging: "financialedge-login-staging.vercel.app",
});
const KNOWN_AUTH_ENVIRONMENT_HOSTS = new Set(Object.values(AUTH_ENVIRONMENT_HOSTS));

function isWebsiteApplicationSource(source) {
    const normalisedSource = String(source || "").trim().toLowerCase();

    return Boolean(
        normalisedSource === "website" ||
        normalisedSource.endsWith("-website") ||
        normalisedSource.includes("website-application")
    );
}

function normaliseAuthEnvironment(value) {
    const normalised = String(value || "").trim().toLowerCase();

    if (normalised === "staging" || normalised === "stage" || normalised === "preview") {
        return "staging";
    }

    if (normalised === "production" || normalised === "prod" || normalised === "live") {
        return "production";
    }

    return "";
}

function getRequestedAuthEnvironment(searchParams) {
    return normaliseAuthEnvironment(
        searchParams.get("authEnv") ||
        searchParams.get("authEnvironment") ||
        searchParams.get("environment")
    );
}

function buildAuthEnvironmentRedirectUrl(searchParams) {
    if (typeof window === "undefined") {
        return "";
    }

    const requestedEnvironment = getRequestedAuthEnvironment(searchParams);
    const targetHostname = AUTH_ENVIRONMENT_HOSTS[requestedEnvironment];
    const currentHostname = String(window.location.hostname || "").trim().toLowerCase();

    if (!targetHostname || currentHostname === targetHostname || !KNOWN_AUTH_ENVIRONMENT_HOSTS.has(currentHostname)) {
        return "";
    }

    const url = new URL(window.location.href);
    url.protocol = "https:";
    url.hostname = targetHostname;
    url.port = "";
    return url.toString();
}

function isIntroAcknowledged(searchParams) {
    const value = String(searchParams.get(WEBSITE_APPLICATION_INTRO_ACK_PARAM) || "").trim().toLowerCase();
    return value === WEBSITE_APPLICATION_INTRO_ACK_VALUE || value === "true" || value === "1";
}

function buildLoginPathWithIntroAcknowledged(search) {
    const params = new URLSearchParams(search);
    params.set(WEBSITE_APPLICATION_INTRO_ACK_PARAM, WEBSITE_APPLICATION_INTRO_ACK_VALUE);
    const query = params.toString();

    return query ? `/login?${query}` : `/login?${WEBSITE_APPLICATION_INTRO_ACK_PARAM}=${WEBSITE_APPLICATION_INTRO_ACK_VALUE}`;
}

function parseTestMobileList(value) {
    return Array.from(
        new Set(
            String(value || "")
                .split(",")
                .map((entry) => toUserId(normalizePhone(entry)))
                .filter(Boolean)
        )
    );
}

function formatDestinationReference(label) {
    const value = String(label || "").trim();
    if (ARTICLELESS_PORTAL_NAMES.has(value)) {
        return value;
    }
    if (ARTICLE_PORTAL_NAMES.has(value)) {
        return `the ${value}`;
    }

    return `the ${value.toLowerCase()}`;
}

function formatPortalSessionReference(shortLabel) {
    const value = String(shortLabel || "").trim();
    if (ARTICLELESS_PORTAL_NAMES.has(value) || ARTICLE_PORTAL_NAMES.has(value)) {
        return `${value} session`;
    }

    return `${value.toLowerCase()} portal session`;
}

function formatSessionTitleReference(shortLabel) {
    const value = String(shortLabel || "").trim();
    if (ARTICLELESS_PORTAL_NAMES.has(value) || ARTICLE_PORTAL_NAMES.has(value)) {
        return `${value} session`;
    }

    return `${value.toLowerCase()} session`;
}

function formatIncomingAuthCodeForDisplay(code, isXPortalWorkspaceLaunch = false) {
    const trimmedCode = String(code || "").trim();
    if (!trimmedCode) {
        return "";
    }

    if (!isXPortalWorkspaceLaunch) {
        return trimmedCode;
    }

    return XPORTAL_LAYER_AUTH_CODE_LABELS[trimmedCode] || trimmedCode;
}

function getIncomingAuthNotice(reason, { portalSessionReference, sessionTitleReference, isXPortalWorkspaceLaunch = false }) {
    const xPortalWorkspaceRetry = "Sign in again and xPortal will open the correct broker or lender workspace for this account.";

    switch (reason) {
        case "missing_session":
            if (isXPortalWorkspaceLaunch) {
                return {
                    tone: "warning",
                    title: "xPortal workspace sign-in required",
                    body: `Your previous xPortal handoff could not be continued. ${xPortalWorkspaceRetry}`,
                };
            }

            return {
                tone: "warning",
                title: `Previous ${sessionTitleReference} ended`,
                body: `Your ${portalSessionReference} was not available for this handoff. Sign in again to continue.`,
            };
        case "refresh_failed":
            if (isXPortalWorkspaceLaunch) {
                return {
                    tone: "warning",
                    title: "xPortal workspace sign-in required",
                    body: `We could not safely refresh the previous xPortal workspace handoff. ${xPortalWorkspaceRetry}`,
                };
            }

            return {
                tone: "warning",
                title: `Previous ${sessionTitleReference} ended`,
                body: `Your ${portalSessionReference} could not be refreshed safely. Sign in again to continue.`,
            };
        case "session_expired":
            return {
                tone: "warning",
                title: `Previous ${sessionTitleReference} expired`,
                body: `This secure handoff has expired. Sign in again and try opening the workspace again.`,
            };
        case "session_revoked":
            return {
                tone: "warning",
                title: `Previous ${sessionTitleReference} ended`,
                body: `This secure handoff is no longer active. Sign in again if you still need access.`,
            };
        case "shared_logout_failed":
            return {
                tone: "warning",
                title: "Previous sign-out needs one more step",
                body: "We could not confirm the shared sign-in session ended before returning you here. Request a new code to continue, or close this browser tab and try again if no code arrives.",
            };
        case "access_denied":
            return {
                tone: "error",
                title: "Access not available",
                body: "This account does not have access to the requested workspace. Contact Financial Edge AX if access should be available.",
            };
        case "session_unable_to_validate":
            return {
                tone: "warning",
                title: `We couldn't check your ${sessionTitleReference}`,
                body: "We could not reach the secure sign-in service to validate this handoff. Try again shortly. If this continues, contact Financial Edge AX.",
            };
        case "session_invalid":
            return {
                tone: "warning",
                title: `We couldn't open this ${sessionTitleReference}`,
                body: "We could not validate this secure handoff. Sign in again and try opening the workspace again. If you do not have access or this keeps happening, contact Financial Edge AX.",
            };
        default:
            return {
                tone: "warning",
                title: `Previous ${sessionTitleReference} ended`,
                body: `Your previous ${portalSessionReference} could not be continued safely. Sign in again and try opening the workspace again.`,
            };
    }
}

export default function Login({ launchContextOverride = null }) {
    const navigate = useNavigate();
    const location = useLocation();
    const launchContext = {
        ...getEffectiveLaunchContext(location.search),
        ...(launchContextOverride || {}),
    };
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const authEnvironmentRedirectUrl = useMemo(
        () => buildAuthEnvironmentRedirectUrl(searchParams),
        [searchParams]
    );
    const usesEmailAuth = isEmailFirstIntent(launchContext.intent);
    const requestedChannel = usesEmailAuth ? "email" : "sms";
    const incomingAuthReason = searchParams.get("authReason") || "";
    const incomingAuthCode = searchParams.get("authCode") || "";
    const incomingAuthRequestId = searchParams.get("requestId") || "";
    const intentDetails = getIntentDetails(launchContext.intent, launchContext.source);
    const destinationReference = formatDestinationReference(intentDetails.label);
    const portalSessionReference = formatPortalSessionReference(intentDetails.shortLabel);
    const sessionTitleReference = formatSessionTitleReference(intentDetails.shortLabel);
    const isXPortalWorkspaceLaunch = isXPortalLayerLaunch(launchContext.intent, launchContext.source);
    const incomingAuthDisplayCode = formatIncomingAuthCodeForDisplay(incomingAuthCode, isXPortalWorkspaceLaunch);
    const incomingAuthNotice = useMemo(
        () => incomingAuthReason
            ? getIncomingAuthNotice(incomingAuthReason, { portalSessionReference, sessionTitleReference, isXPortalWorkspaceLaunch })
            : null,
        [incomingAuthReason, isXPortalWorkspaceLaunch, portalSessionReference, sessionTitleReference]
    );
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState(() => usesEmailAuth ? readRememberedSignInEmail() : "");
    const [rememberDevice, setRememberDevice] = useState(() => {
        const storedPreference = readRememberDevicePreference();
        return storedPreference === null ? launchContext.rememberDevice === true : storedPreference;
    });
    const [error, setError] = useState("");
    const [errorCode, setErrorCode] = useState("");
    const [errorRequestId, setErrorRequestId] = useState("");
    const [loading, setLoading] = useState(false);
    const testMobiles = useMemo(
        () => parseTestMobileList(process.env.REACT_APP_AUTH_TEST_MOBILES),
        []
    );
    const fixedTestOtp = useMemo(
        () => String(process.env.REACT_APP_AUTH_TEST_FIXED_OTP || "").trim(),
        []
    );
    const showTestUserHelper = process.env.NODE_ENV !== "production" && testMobiles.length > 0;
    const credentialInputClassName =
        "border-lightBorder bg-white shadow-sm transition focus:border-primary focus:ring-2 focus:ring-primary/30";
    const showWebsiteApplicationIntro =
        launchContext.intent === "application" &&
        isWebsiteApplicationSource(launchContext.source) &&
        !isIntroAcknowledged(searchParams) &&
        !incomingAuthReason;

    useAuthPageMeta(
        showWebsiteApplicationIntro ? "Start lender matching" : "Secure Sign In",
        showWebsiteApplicationIntro
            ? "A short introduction before the secure Financial Edge AX application form."
            : undefined
    );

    useEffect(() => {
        if (authEnvironmentRedirectUrl) {
            window.location.replace(authEnvironmentRedirectUrl);
            return;
        }

        if (launchContext.hasExplicitIntent && !launchContext.isValidIntent) {
            navigate(`/entry${location.search || ""}`, { replace: true });
        }
    }, [authEnvironmentRedirectUrl, launchContext.hasExplicitIntent, launchContext.isValidIntent, location.search, navigate]);

    useEffect(() => {
        persistRememberDevicePreference(rememberDevice);
    }, [rememberDevice]);

    const canContinue = useMemo(() => {
        if (loading) {
            return false;
        }

        return usesEmailAuth ? isValidEmail(email) : isValidAuMobile(phone);
    }, [email, loading, phone, usesEmailAuth]);

    const handleContinue = async (event) => {
        event?.preventDefault?.();
        setError("");
        setErrorCode("");
        setErrorRequestId("");
        setLoading(true);

        try {
            // The bare /login route already presents the application form as the chosen destination.
            // Preserve that choice through OTP verification so post-login routing does not reopen workspace selection.
            const submittedHasExplicitIntent = launchContext.hasExplicitIntent || launchContext.intent === "application";
            const formattedPhone = usesEmailAuth ? null : normalizePhone(phone);
            const formattedEmail = usesEmailAuth ? String(email || "").trim().toLowerCase() : null;

            if (usesEmailAuth) {
                if (!isValidEmail(formattedEmail)) {
                    setError("Enter a valid email address.");
                    return;
                }
            } else if (!isValidAuMobile(formattedPhone)) {
                setError("Enter a valid mobile number, for example 04xx xxx xxx.");
                return;
            }

            const otpResponse = await api.requestOtp({
                phone: formattedPhone,
                email: formattedEmail,
                intent: launchContext.intent,
                source: launchContext.source,
                brokerageId: launchContext.brokerageId,
                returnTo: launchContext.returnTo,
                hasExplicitIntent: submittedHasExplicitIntent,
                channel: requestedChannel,
            });
            const resolvedIntent = resolveXPortalLayerIntentFromAuthResponse(
                otpResponse,
                launchContext.intent,
                launchContext.source
            );
            const resolvedReturnTo = resolveXPortalLayerReturnTo(
                launchContext.returnTo,
                launchContext.intent,
                resolvedIntent,
                launchContext.source
            );
            const resolvedLaunchContext = {
                ...launchContext,
                intent: resolvedIntent,
                returnTo: resolvedReturnTo,
            };

            persistLaunchContext({
                ...resolvedLaunchContext,
                hasExplicitIntent: submittedHasExplicitIntent,
                rememberDevice,
            });

            const challengeChannel = otpResponse?.challenge?.channel || requestedChannel;
            const challengeState = {
                phone: formattedPhone,
                email: formattedEmail,
                requestId: otpResponse?.requestId || null,
                challengeId: otpResponse?.challenge?.challengeId || null,
                channel: challengeChannel,
                otpLength: getOtpLengthForChallenge(otpResponse?.challenge),
                otpPreview: otpResponse?.challenge?.otpPreview || null,
                testUserBypass: otpResponse?.challenge?.testUserBypass === true,
                brokerageId: resolvedLaunchContext.brokerageId,
                intent: resolvedIntent,
                source: launchContext.source,
                returnTo: resolvedReturnTo,
                hasExplicitIntent: submittedHasExplicitIntent,
                rememberDevice,
                emailDestinationHint:
                    challengeChannel === "email"
                        ? (otpResponse?.challenge?.delivery?.redactedDestination || otpResponse?.methodSelection?.email?.destinationHint || null)
                        : null,
                fallbackReason: otpResponse?.challenge?.fallbackReason || null,
                methodSelection: otpResponse?.methodSelection || null,
            };
            persistPendingVerificationContext(challengeState);

            if (isBlockedSignInInterception(otpResponse?.access)) {
                navigate(`/auth-status${buildLaunchSearch(resolvedLaunchContext) || ""}`, {
                    replace: true,
                    state: buildBlockedSignInStatusState(otpResponse.access, {
                        intent: resolvedIntent,
                    }),
                });
                return;
            }

            navigate(`/verify-code${buildLaunchSearch(resolvedLaunchContext)}`, {
                state: challengeState,
            });
        } catch (err) {
            if (err?.code === "AUTH_IPORTAL_ACCESS_REQUIRED" && launchContext.intent === "investor") {
                const attemptedEmail = usesEmailAuth ? String(email || err?.details?.email || "").trim().toLowerCase() : "";

                logAuthFlowWarning("iportal_access_request_required", {
                    intent: launchContext.intent,
                    source: launchContext.source,
                    email: attemptedEmail || null,
                    requestId: err?.requestId || null,
                });

                navigate(`/investor-access-request${buildLaunchSearch(launchContext)}`, {
                    state: {
                        intent: launchContext.intent,
                        source: launchContext.source,
                        returnTo: launchContext.returnTo,
                        email: attemptedEmail,
                        accessDetails: err?.details || null,
                    },
                });
                return;
            }

            logAuthFlowWarning("login_failed", {
                intent: launchContext.intent,
                source: launchContext.source,
                message: err?.message || "unknown_error",
                code: err?.code || null,
                requestId: err?.requestId || null,
            });
            setError(getSignInErrorMessage(err));
            setErrorCode(getAuthErrorCode(err) || "");
            setErrorRequestId(getAuthRequestId(err) || "");
        } finally {
            setLoading(false);
        }
    };

    if (showWebsiteApplicationIntro) {
        return (
            <WebsiteApplicationIntro
                source={launchContext.source}
                onContinue={() => {
                    navigate(buildLoginPathWithIntroAcknowledged(location.search), { replace: true });
                }}
            />
        );
    }

    if (authEnvironmentRedirectUrl) {
        return (
            <AuthShell
                eyebrow="Secure access"
                title="Opening secure sign-in"
                description="We are switching to the correct sign-in environment for this workspace."
                align="center"
            />
        );
    }

    return (
        <AuthShell
            eyebrow="Step 1 of 3"
            title={usesEmailAuth ? "Verify your email address" : "Verify your mobile number"}
            description={usesEmailAuth
                ? `Sign in securely to continue to ${destinationReference}. We will email you a one-time code to the approved address on file.`
                : `Sign in securely to continue to ${destinationReference}. We will send a one-time code to your verified mobile number.`}
            footer={
                <div className="space-y-3 text-sm text-secondary">
                    <p>Your code is used only to verify your identity and prepare the correct Financial Edge AX workspace.</p>
                    {!launchContext.hasExplicitIntent ? (
                        <button
                            type="button"
                            onClick={() => navigate(`/entry${buildLaunchSearch(launchContext)}`)}
                            className="font-semibold text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                        >
                            Choose a different destination
                        </button>
                    ) : null}
                </div>
            }
        >
            <form onSubmit={handleContinue} noValidate>
                <AuthContextPanel
                    title={`Continue to ${intentDetails.label}`}
                    items={[
                        {
                            label: "Workspace",
                            value: intentDetails.label,
                        },
                    ]}
                    helper="We use this step only to send a one-time code and prepare the right workspace."
                />

                {incomingAuthNotice ? (
                    <div className="mt-4 space-y-2">
                        <AuthAlert tone={incomingAuthNotice.tone} title={incomingAuthNotice.title}>
                            {incomingAuthNotice.body}
                        </AuthAlert>
                        {incomingAuthDisplayCode ? (
                            <div className="text-xs text-secondary">
                                Error code: <span className="font-mono text-foreground">{incomingAuthDisplayCode}</span>
                            </div>
                        ) : null}
                        {incomingAuthRequestId ? (
                            <div className="text-xs text-secondary">
                                Request ID: <span className="font-mono text-foreground">{incomingAuthRequestId}</span>
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {showTestUserHelper && !usesEmailAuth ? (
                    <div className="mt-5 border-y border-dashed border-lightBorder py-3 text-sm text-secondary">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                                <p className="font-semibold text-foreground">Development test users</p>
                                <p className="mt-1">Pick a number to fill the mobile field.</p>
                            </div>
                            {fixedTestOtp ? (
                                <p className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-primary">
                                    OTP: {fixedTestOtp}
                                </p>
                            ) : null}
                        </div>

                        <div className="mt-3 flex max-h-24 flex-wrap gap-2 overflow-y-auto pr-1">
                            {testMobiles.map((mobile) => (
                                <button
                                    key={mobile}
                                    type="button"
                                    onClick={() => {
                                        setPhone(formatDisplayNumber(mobile));
                                        setError("");
                                        setErrorCode("");
                                        setErrorRequestId("");
                                    }}
                                    className="rounded-full border border-lightBorder bg-white px-3 py-1 text-sm font-medium text-foreground transition hover:border-primary hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                                >
                                    {formatDisplayNumber(mobile)}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : null}

                <div className="mt-6">
                    {usesEmailAuth ? (
                        <InputField
                            label="Email address"
                            placeholder="name@company.com"
                            type="email"
                            value={email}
                            onChange={(val) => {
                                setEmail(val);
                                setError("");
                                setErrorCode("");
                                setErrorRequestId("");
                            }}
                            className="w-full text-xl"
                            inputClassName={credentialInputClassName}
                        />
                    ) : (
                        <InputField
                            label="Mobile number"
                            placeholder="04xx xxx xxx"
                            type="tel"
                            value={phone}
                            parseOnChange={parsePhoneInput}
                            onChange={(val) => {
                                setPhone(formatDisplayNumber(val));
                                setError("");
                                setErrorCode("");
                                setErrorRequestId("");
                            }}
                            className="w-full text-xl"
                            inputClassName={credentialInputClassName}
                        />
                    )}
                </div>

                <label className="mt-5 flex items-start gap-3 rounded-2xl border border-lightBorder bg-surface px-4 py-3 text-sm text-secondary">
                    <input
                        type="checkbox"
                        checked={rememberDevice}
                        onChange={(event) => setRememberDevice(event.target.checked)}
                        className="mt-1 h-4 w-4 rounded border-lightBorder text-primary focus:ring-primary"
                    />
                    <span>
                        <span className="block font-semibold text-foreground">Keep me signed in on this device</span>
                        <span className="mt-1 block">Use this only on a device you trust.</span>
                    </span>
                </label>

                <p className="mt-3 text-sm text-secondary">
                    {usesEmailAuth
                        ? "We do not show or store your verification code in this browser."
                        : "Standard carrier charges may apply. We do not show or store your verification code in this browser."}
                </p>

                {error ? (
                    <div className="mt-4 space-y-2">
                        <AuthAlert tone="error" title="Sign-in could not continue" live="assertive">
                            {error}
                        </AuthAlert>
                        {errorCode ? (
                            <div className="text-xs text-secondary">
                                Error code: <span className="font-mono text-foreground">{errorCode}</span>
                            </div>
                        ) : null}
                        {errorRequestId ? (
                            <div className="text-xs text-secondary">
                                Request ID: <span className="font-mono text-foreground">{errorRequestId}</span>
                            </div>
                        ) : null}
                    </div>
                ) : null}

                <div className="mt-8">
                    <PrimaryButton type="submit" disabled={!canContinue || loading} aria-busy={loading}>
                        {loading ? "Sending secure code..." : "Send secure code"}
                    </PrimaryButton>
                </div>
            </form>
        </AuthShell>
    );
}
